import { Pipe, PipeTransform } from "@angular/core";
import { MeasurementUtilsService } from "@core/services/measurement-utils/measurement-utils.service";

import { MeasurementData, MeasurementPoint } from "./measuring-tool.component";

@Pipe({
  name: "calculateDistance",
  pure: true
})
export class CalculateDistancePipe implements PipeTransform {
  transform(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  }
}

@Pipe({
  name: "formatCoordinatesCompact",
  pure: true
})
export class FormatCoordinatesCompactPipe implements PipeTransform {
  transform(ra: number | null, dec: number | null): string {
    // If either coordinate is null, return empty string
    if (ra === null || dec === null) {
      return "";
    }

    // Convert ra/dec to formatted strings
    const raHours = Math.floor(ra);
    const raMinutesFull = (ra - raHours) * 60;
    const raMinutes = Math.floor(raMinutesFull);
    const raSecondsFull = (raMinutesFull - raMinutes) * 60;
    // Round to nearest second for consistent display with verbose formatter
    const raSeconds = Math.round(raSecondsFull);

    // Handle carryover when seconds round up to 60
    let finalRaHours = raHours;
    let finalRaMinutes = raMinutes;
    let finalRaSeconds = raSeconds;

    if (finalRaSeconds >= 60) {
      finalRaSeconds = 0;
      finalRaMinutes += 1;

      if (finalRaMinutes >= 60) {
        finalRaMinutes = 0;
        finalRaHours += 1;

        if (finalRaHours >= 24) {
          finalRaHours = 0;
        }
      }
    }

    // Do the same for declination
    const decDegrees = Math.abs(dec);
    const decSign = dec >= 0 ? "+" : "-";
    const decDegreesInt = Math.floor(decDegrees);
    const decMinutesFull = (decDegrees - decDegreesInt) * 60;
    const decMinutesInt = Math.floor(decMinutesFull);
    const decSecondsFull = (decMinutesFull - decMinutesInt) * 60;
    // Round to nearest second for consistent display
    const decSeconds = Math.round(decSecondsFull);

    // Handle carryover when seconds round up to 60
    let finalDecDegrees = decDegreesInt;
    let finalDecMinutes = decMinutesInt;
    let finalDecSeconds = decSeconds;

    if (finalDecSeconds >= 60) {
      finalDecSeconds = 0;
      finalDecMinutes += 1;

      if (finalDecMinutes >= 60) {
        finalDecMinutes = 0;
        finalDecDegrees += 1;
      }
    }

    return `${finalRaHours.toString().padStart(2, "0")}h ${finalRaMinutes.toString().padStart(2, "0")}m ${finalRaSeconds
      .toString()
      .padStart(2, "0")}s, ${decSign}${finalDecDegrees.toString().padStart(2, "0")}° ${finalDecMinutes
      .toString()
      .padStart(2, "0")}' ${finalDecSeconds.toString().padStart(2, "0")}"`;
  }
}

@Pipe({
  name: "mathMin",
  pure: true
})
export class MathMinPipe implements PipeTransform {
  transform(a: number, b: number): number {
    return Math.min(a, b);
  }
}

@Pipe({
  name: "mathMax",
  pure: true
})
export class MathMaxPipe implements PipeTransform {
  transform(a: number, b: number): number {
    return Math.max(a, b);
  }
}

@Pipe({
  name: "mathAbs",
  pure: true
})
export class MathAbsPipe implements PipeTransform {
  transform(value: number): number {
    return Math.abs(value);
  }
}

@Pipe({
  name: "getMidpoint",
  pure: true
})
export class GetMidpointPipe implements PipeTransform {
  transform(a: number, b: number): number {
    return (a + b) / 2;
  }
}

interface LabelPosition {
  x: number;
  y: number;
}

@Pipe({
  name: "calculateLabelPosition",
  pure: true
})
export class CalculateLabelPositionPipe implements PipeTransform {
  constructor(private measurementUtilsService: MeasurementUtilsService) {}

  transform(
    startPoint: MeasurementPoint | null,
    endPoint: MeasurementPoint | null,
    position: "start" | "end"
  ): LabelPosition {
    if (!startPoint || !endPoint) {
      return { x: 0, y: 0 };
    }

    // Create a temporary measurement object
    const measurement: MeasurementData = {
      startX: startPoint.x,
      startY: startPoint.y,
      endX: endPoint.x,
      endY: endPoint.y,
      midX: (startPoint.x + endPoint.x) / 2,
      midY: (startPoint.y + endPoint.y) / 2,
      distance: "", // This isn't needed for label calculation
      timestamp: Date.now(),
      startRa: startPoint.ra,
      startDec: startPoint.dec,
      endRa: endPoint.ra,
      endDec: endPoint.dec,
      startLabelX: 0,
      startLabelY: 0,
      endLabelX: 0,
      endLabelY: 0,
      showRectangle: true // To ensure dimension labels are created
    };

    // Let the service calculate the positions
    this.measurementUtilsService.updateCoordinateLabelPositions(measurement);

    // Return the requested position
    return position === "start"
      ? { x: measurement.startLabelX, y: measurement.startLabelY }
      : { x: measurement.endLabelX, y: measurement.endLabelY };
  }
}
